import type { Witness } from '../store/booksStore'
import type { LedgerSafeEditorialEvent } from '../types/ledgerSafeEditorialEvents'
import { getRowLabel, type AnchoredRow } from '../types/anchoredRowTypes'
import {
  canCollectMoreLedgerSafeSuggestions,
  collectLedgerSafeEventSuggestions,
  finalizeLedgerSafeEditSuggestions
} from './ledgerSafeEditSuggestionCollector'
import {
  buildLedgerSafeAppliedOperationKeySet,
  findReusableLedgerSafeInsertionSlotRowId,
  getRecentLedgerSafeEditorialEvents
} from './ledgerSafeEditSuggestionEvents'
import {
  buildLedgerSafeBoundaryShiftSuggestion,
  buildLedgerSafeMergeUnitsSuggestion,
  buildLedgerSafeSplitUnitSuggestion,
  findLedgerSafeBoundaryRightRowId
} from './ledgerSafeEditSuggestionPreview'
import { buildLedgerSafeRowMotionSuggestion } from './ledgerSafeEditSuggestionRowMotion'
import {
  getLedgerSafeActiveMappingText,
  sortLedgerSafeSuggestionRows
} from './ledgerSafeEditSuggestionRows'
import {
  DEFAULT_MAX_LEDGER_SAFE_SUGGESTIONS_PER_EVENT
} from './ledgerSafeEditSuggestionScoring'
import {
  parseLedgerSafeBoundaryShiftSuggestionEvent,
  parseLedgerSafeMergeSuggestionEvent,
  parseLedgerSafeRowMotionSuggestionEvent,
  parseLedgerSafeSplitSuggestionEvent
} from './ledgerSafeEditSuggestionRouting'
import type {
  LedgerSafeEditSuggestion,
  LedgerSafeEditSuggestionBuildInput
} from './ledgerSafeEditSuggestionTypes'

export type {
  LedgerSafeEditSuggestion,
  LedgerSafeEditSuggestionBuildInput,
  LedgerSafeEditSuggestionKind
} from './ledgerSafeEditSuggestionTypes'

type ReuseSuggestionCandidate = (
  event: LedgerSafeEditorialEvent,
  source: Witness | undefined,
  target: Witness,
  sourceRowIds: readonly string[],
  build: () => LedgerSafeEditSuggestion | null
) => LedgerSafeEditSuggestion | null

export const buildLedgerSafeEditSuggestions = ({
  rows,
  witnesses,
  editorialEvents,
  dismissedSuggestionIds,
  maxSuggestions = 24,
  maxSourceEvents = 12,
  maxSuggestionsPerEvent = DEFAULT_MAX_LEDGER_SAFE_SUGGESTIONS_PER_EVENT
}: LedgerSafeEditSuggestionBuildInput, reuseCandidate: ReuseSuggestionCandidate = (_event, _source, _target, _sourceRowIds, build) => build()): LedgerSafeEditSuggestion[] => {
  const sortedRows = sortLedgerSafeSuggestionRows(rows)
  const rowsById = new Map(sortedRows.map(row => [row.id, row]))
  const witnessesById = new Map(witnesses.map(witness => [witness.id, witness]))
  const dismissedIds = new Set(dismissedSuggestionIds || [])
  const appliedKeys = buildLedgerSafeAppliedOperationKeySet(editorialEvents)
  const suggestions: LedgerSafeEditSuggestion[] = []

  const collectCandidates = (candidateSuggestions: Array<LedgerSafeEditSuggestion | null>) => {
    collectLedgerSafeEventSuggestions({
      suggestions,
      candidateSuggestions,
      dismissedIds,
      appliedKeys,
      maxSuggestionsPerEvent
    })
  }

  getRecentLedgerSafeEditorialEvents(editorialEvents, maxSourceEvents).forEach(event => {
    if (!canCollectMoreLedgerSafeSuggestions(suggestions, maxSuggestions)) {
      return
    }
    const kind = event.operation?.kind

    if (kind === 'boundary_shift') {
      const parsed = parseLedgerSafeBoundaryShiftSuggestionEvent(event, rowsById)
      if (!parsed) {
        return
      }
      const sourceWitness = witnessesById.get(parsed.sourceWitnessId)
      const targets = witnesses.filter(witness => witness.id !== parsed.sourceWitnessId)
      collectCandidates(targets.map(targetWitness => {
        const rightRowId = findLedgerSafeBoundaryRightRowId(sortedRows, parsed.leftRowId, targetWitness.id)
        return reuseCandidate(event, sourceWitness, targetWitness, [parsed.leftRowId, ...(rightRowId ? [rightRowId] : [])], () => buildLedgerSafeBoundaryShiftSuggestion({
          event,
          rows: sortedRows,
          targetWitness,
          sourceWitness,
          leftRowId: parsed.leftRowId,
          rightRowId,
          deltaWords: parsed.deltaWords
        }))
      }))
      return
    }

    if (kind === 'merge_units') {
      const parsed = parseLedgerSafeMergeSuggestionEvent(event, rowsById)
      if (!parsed) {
        return
      }
      const sourceWitness = witnessesById.get(parsed.sourceWitnessId)
      const targets = witnesses.filter(witness => witness.id !== parsed.sourceWitnessId)
      collectCandidates(targets.map(targetWitness => (
        reuseCandidate(event, sourceWitness, targetWitness, parsed.rowIds, () => buildLedgerSafeMergeUnitsSuggestion({
          event,
          rows: sortedRows,
          targetWitness,
          sourceWitness,
          rowIds: parsed.rowIds
        }))
      )))
      return
    }

    if (kind === 'split_unit') {
      const parsed = parseLedgerSafeSplitSuggestionEvent(event, rowsById)
      if (!parsed) {
        return
      }
      const sourceWitness = witnessesById.get(parsed.sourceWitnessId)
      const targets = witnesses.filter(witness => witness.id !== parsed.sourceWitnessId)
      collectCandidates(targets.map(targetWitness => (
        reuseCandidate(event, sourceWitness, targetWitness, [parsed.rowId], () => buildLedgerSafeSplitUnitSuggestion({
          event,
          rows: sortedRows,
          targetWitness,
          sourceWitness,
          rowId: parsed.rowId,
          afterWordIndex: parsed.afterWordIndex
        }))
      )))
      return
    }

    const rowMotion = parseLedgerSafeRowMotionSuggestionEvent(event, rowsById)
    if (!rowMotion) {
      return
    }
    const sourceWitness = witnessesById.get(rowMotion.sourceWitnessId)
    const targets = witnesses.filter(witness => witness.id !== rowMotion.sourceWitnessId)

    if (rowMotion.kind === 'insert_row_and_move') {
      const rowId = String(rowMotion.params.rowId || '')
      const placement = rowMotion.params.placement === 'above' ? 'above' : 'below'
      const reusableSlotRowId = findReusableLedgerSafeInsertionSlotRowId({
        rows: sortedRows,
        editorialEvents,
        event,
        sourceWitnessId: rowMotion.sourceWitnessId,
        rowId,
        placement
      })
      if (reusableSlotRowId) {
        const direction = placement === 'above' ? 'up' : 'down'
        collectCandidates(targets.map(targetWitness => (
          buildLedgerSafeRowMotionSuggestion({
            event,
            rows: sortedRows,
            targetWitness,
            sourceWitness,
            kind: 'move_mapping',
            rowIds: rowMotion.rowIds,
            params: {
              rowId,
              direction,
              sourceOperationKind: 'insert_row_and_move',
              sourceSlotRowId: reusableSlotRowId
            }
          })
        )))
        return
      }
    }

    collectCandidates(targets.map(targetWitness => (
      buildLedgerSafeRowMotionSuggestion({
        event,
        rows: sortedRows,
        targetWitness,
        sourceWitness,
        kind: rowMotion.kind,
        rowIds: rowMotion.rowIds,
        params: rowMotion.params
      })
    )))
  })

  return finalizeLedgerSafeEditSuggestions(suggestions, maxSuggestions)
}

/** One editor owns one generation of candidate results. Exact text signatures
 * make reuse safe even when callers replace or mutate nested input objects. */
export const createLedgerSafeEditSuggestionBuilder = () => {
  type Entry = { inputs: string[]; result: LedgerSafeEditSuggestion | null }
  let previous = new Map<string, Entry>()
  return (input: LedgerSafeEditSuggestionBuildInput): LedgerSafeEditSuggestion[] => {
    const rows = sortLedgerSafeSuggestionRows(input.rows)
    const rowShape = JSON.stringify(rows.map(({ unitMappings, updatedAt, createdAt, ...row }) => row))
    const witnessInputs = new Map<string, string>()
    const eventInputs = new Map<LedgerSafeEditorialEvent, string>()
    const next = new Map<string, Entry>()
    const signature = (witness: Witness | undefined, sourceRowIds?: readonly string[], includeSourceContext = true): string => {
      if (!witness) return ''
      const key = JSON.stringify([witness.id, sourceRowIds, includeSourceContext])
      if (!witnessInputs.has(key)) {
        const relevantRows = sourceRowIds ? rows.filter(row => sourceRowIds.includes(row.id)) : rows
        witnessInputs.set(key, JSON.stringify([
          witness.id, witness.sigla, witness.description,
          includeSourceContext ? witness.segmentation_result : null,
          includeSourceContext ? witness.uploaded_file?.content : null,
          relevantRows.map(row => row.unitMappings.filter(mapping => mapping.witnessId === witness.id)),
        ]))
      }
      return witnessInputs.get(key)!
    }
    const result = buildLedgerSafeEditSuggestions(input, (event, source, target, sourceRowIds, build) => {
      if (!eventInputs.has(event)) eventInputs.set(event, JSON.stringify(event))
      const key = `${event.id}:${target.id}`
      const inputs = [rowShape, eventInputs.get(event)!, signature(source, sourceRowIds, event.operation?.kind === 'split_unit'), signature(target)]
      const cached = previous.get(key)
      const result = cached && inputs.every((value, index) => value === cached.inputs[index])
        ? cached.result
        : build()
      next.set(key, { inputs, result })
      return result
    })
    previous = next
    return result
  }
}

export const indexLedgerSafeEditSuggestionsByCell = (
  suggestions: LedgerSafeEditSuggestion[]
): Map<string, LedgerSafeEditSuggestion> => {
  const byCell = new Map<string, LedgerSafeEditSuggestion>()
  suggestions.forEach(suggestion => {
    suggestion.rowIds.forEach(rowId => {
      const key = `${rowId}:${suggestion.targetWitnessId}`
      if (!byCell.has(key)) {
        byCell.set(key, suggestion)
      }
    })
  })
  return byCell
}

export const buildLedgerSafeEditSuggestionActionKey = (
  suggestion: LedgerSafeEditSuggestion
): string => `${suggestion.primaryRowId}:${suggestion.targetWitnessId}`

export const summarizeLedgerSafeEditSuggestion = (
  suggestion: LedgerSafeEditSuggestion,
  rows: AnchoredRow[]
): string => {
  const rowLabels = suggestion.rowIds
    .map(rowId => rows.find(row => row.id === rowId))
    .filter((row): row is AnchoredRow => Boolean(row))
    .map(getRowLabel)
  if (rowLabels.length === 0) {
    return suggestion.label
  }
  return `${suggestion.label} at ${rowLabels.join(' + ')}`
}

export const buildLedgerSafeSuggestionComparisonText = ({
  rows,
  suggestion
}: {
  rows: AnchoredRow[]
  suggestion: LedgerSafeEditSuggestion
}): string => {
  const snippets = suggestion.rowIds
    .map(rowId => getLedgerSafeActiveMappingText(rows.find(row => row.id === rowId), suggestion.targetWitnessId))
    .filter(Boolean)
    .map(text => text.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
  return snippets.join(' / ')
}
